// Único lugar que sabe armar la ventana de impresión y los 3 formatos de
// página (térmica 100x150, 100x190, A4 2x2) para una etiqueta Lynx. Tanto
// la impresión individual (packageLabel.ts) como la masiva
// (printLabelsBatch.ts) consumen esto -- ninguna de las dos arma HTML/CSS
// de impresión por su cuenta, para que no existan dos lugares que puedan
// divergir en cómo se ve una etiqueta impresa (ver auditoría "LYNX --
// cambio mínimo External API/etiquetas").

export type LabelPrintFormat = "a4" | "100x150" | "100x190";

export const LABEL_PRINT_FORMATS: { value: LabelPrintFormat; label: string }[] = [
  { value: "a4", label: "A4" },
  { value: "100x150", label: "100 × 150 mm" },
  { value: "100x190", label: "100 × 190 mm" },
];

// La térmica mide 400x600 (ver labelService.js, escala 4u = 1mm, 100x150mm
// real) y la celda A4 540x210 (horizontal, layout propio de 3 columnas) --
// nunca se estiran, cada SVG trae su propio viewBox y el navegador lo
// encaja proporcionalmente solo (preserveAspectRatio por defecto). El
// aspect-ratio de `.label-cell` tiene que coincidir con el viewBox real de
// lo que labelSvgPages() elija para "a4" (ver bug corregido: antes decía
// 400/600, la proporción de la térmica, porque nunca se había actualizado
// cuando labelService.js agregó un renderer A4 propio -- el formato A4
// terminaba imprimiendo térmicas verticales encajadas de a 2 por fila).
const FORMAT_STYLES: Record<LabelPrintFormat, string> = {
  a4: `
    @page { size: A4; margin: 10mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: system-ui, sans-serif; }
    .labels-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6mm; }
    .label-cell { aspect-ratio: 540 / 210; break-inside: avoid; page-break-inside: avoid; }
    .label-cell svg { width: 100%; height: 100%; display: block; }
  `,
  "100x150": `
    @page { size: 100mm 150mm; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: system-ui, sans-serif; }
    .label-page { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; break-after: page; page-break-after: always; }
    .label-page:last-of-type { break-after: auto; page-break-after: auto; }
    .label-page svg { width: 100%; height: 100%; display: block; }
  `,
  "100x190": `
    @page { size: 100mm 190mm; margin: 5mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: system-ui, sans-serif; }
    .label-page { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; break-after: page; page-break-after: always; }
    .label-page:last-of-type { break-after: auto; page-break-after: auto; }
    .label-page svg { width: 100%; height: 100%; display: block; }
  `,
};

// Cada label puede traer más de un bulto (ver labelService.js,
// cantidad_bultos -> svgs/a4Cells, misma cantidad de elementos en ambos
// arrays). Esta es la ÚNICA función que "desarma" labels con N bultos en
// una lista plana de páginas imprimibles -- ni la impresión individual ni
// la masiva repiten esta lógica. Bug corregido acá (ver "LYNX -- A4 no
// funciona, auditoría con flujo real"): antes esta función ignoraba por
// completo `a4Cells` y devolvía siempre las térmicas, sin importar el
// formato pedido -- el renderer A4 de labelService.js nunca llegaba a
// usarse en el flujo real. Ahora el formato decide qué array de cada
// label se usa; `svg` (singular) sigue de respaldo para una respuesta
// vieja sin `svgs`.
export function labelSvgPages(
  labels: { svgs?: string[]; a4Cells?: string[]; svg: string }[],
  format: LabelPrintFormat
): string[] {
  return labels.flatMap((l) => {
    if (format === "a4" && l.a4Cells && l.a4Cells.length) return l.a4Cells;
    return l.svgs && l.svgs.length ? l.svgs : [l.svg];
  });
}

function buildBody(format: LabelPrintFormat, svgPages: string[]): string {
  if (format === "a4") {
    return `<div class="labels-grid">${svgPages.map((svg) => `<div class="label-cell">${svg}</div>`).join("")}</div>`;
  }
  return svgPages.map((svg) => `<div class="label-page">${svg}</div>`).join("");
}

export function buildPrintableHtml(format: LabelPrintFormat, svgPages: string[], title: string): string {
  return (
    `<!doctype html><html lang="es"><head><meta charset="utf-8">` +
    `<title>${title}</title>` +
    `<style>${FORMAT_STYLES[format]}</style></head>` +
    `<body>${buildBody(format, svgPages)}</body></html>`
  );
}

// Abre la ventana, escribe el HTML y dispara print() una sola vez. Lanza si
// el navegador bloqueó el popup -- el llamador puede usar eso como la
// confirmación real de "la ventana se abrió" antes de marcar
// label_printed_at (ver bug corregido en printLabelsBatch.ts).
export function openPrintWindow(html: string, opts: { width?: number; height?: number } = {}): Window {
  const { width = 900, height = 700 } = opts;
  const win = window.open("", "_blank", `width=${width},height=${height}`);
  if (!win) throw new Error("El navegador bloqueó la ventana de impresión. Permitila y probá de nuevo.");

  win.document.write(html);
  win.document.close();
  win.focus();

  // El print va después del load: si se dispara antes, algunas impresoras
  // sacan la hoja en blanco porque el QR todavía no se dibujó. Una sola
  // vez: el evento de carga y el temporizador de respaldo pueden llegar
  // los dos y abrir dos diálogos de impresión (visto el 15-sep-2026).
  let printed = false;
  const printOnce = () => {
    if (printed) return;
    printed = true;
    try {
      win.print();
    } catch {
      /* el operador imprime a mano */
    }
  };
  win.addEventListener("load", printOnce);
  window.setTimeout(printOnce, 500);

  return win;
}
